import type { CashPaymentStartResponseDTO } from './checkoutApi.js';

/** Readiness-aware cash rail: unknown must not act as denied for settle wipe. */
export type CashRailState = 'unknown' | 'allowed' | 'denied';

export function resolveCashRailState(input: {
  readonly paymentSurfaceReadiness: unknown | null | undefined;
  readonly cashInVisibleMethods: boolean;
}): CashRailState {
  if (input.paymentSurfaceReadiness === null || input.paymentSurfaceReadiness === undefined) {
    return 'unknown';
  }
  return input.cashInVisibleMethods ? 'allowed' : 'denied';
}

function trimPayId(value: string | null | undefined): string {
  return value?.trim() ?? '';
}

function resolveCanonicalJourneyPaymentId(
  journeyPaymentId: string | null,
  checkoutSessionPaymentId: string | null | undefined,
): string {
  const journeyPay = trimPayId(journeyPaymentId);
  if (journeyPay.length > 0) {
    return journeyPay;
  }
  return trimPayId(checkoutSessionPaymentId);
}

/**
 * Whether a persisted/startCash DTO may mount cash-collect chrome.
 *
 * R1:
 * - awaiting===true OR not yet COMPLETED+!awaiting (poll data) → keep (mid-handoff)
 * - COMPLETED+!awaiting (even while refetching) + paymentId match → keep (paid cash T6)
 * - COMPLETED+!awaiting + empty/mismatch → hide (effect clears)
 * - sessionSawAwaitingCash strengthens keep when not an explicit paymentId mismatch
 */
export function shouldExposeCashCollectShell(input: {
  readonly cashCompleteResult: CashPaymentStartResponseDTO;
  readonly journeyPaymentId: string | null;
  readonly checkoutSessionPaymentId: string | null | undefined;
  readonly checkoutSessionStatus: string | null | undefined;
  readonly paymentStatusSettled: boolean;
  readonly paymentStatus: string | undefined;
  readonly awaitingCashConfirmation: boolean | undefined;
  readonly sessionSawAwaitingCash?: boolean;
}): boolean {
  const dtoPay = trimPayId(input.cashCompleteResult.paymentId);
  const journeyPay = resolveCanonicalJourneyPaymentId(
    input.journeyPaymentId,
    input.checkoutSessionPaymentId,
  );
  const paymentIdsMatch = journeyPay.length > 0 && dtoPay.length > 0 && dtoPay === journeyPay;
  const pollAwaiting = input.awaitingCashConfirmation === true;
  // T-INIT: apply match gate when poll *data* already says COMPLETED+!awaiting even if refetching.
  const dataSaysCompletedNotAwait =
    input.paymentStatus === 'COMPLETED' && input.awaitingCashConfirmation !== true;

  // Authoritative staff-cash awaiting — keep shell (mid-handoff / cold resume).
  if (pollAwaiting) {
    return true;
  }

  if (dataSaysCompletedNotAwait) {
    if (dtoPay.length === 0) {
      return false;
    }
    if (paymentIdsMatch) {
      return true;
    }
    if (journeyPay.length > 0 && dtoPay !== journeyPay) {
      return false;
    }
    return input.sessionSawAwaitingCash === true;
  }

  // MOB-05 / remount: DTO awaiting + matching paymentId (sessionStorage / API cash start).
  // Do not require checkoutSessionStatus === 'AWAITING_PAYMENT' — live cash start stamps
  // PAYMENT_INITIATED (API-mapped to AWAITING_PAYMENT), but status can lag on first paint;
  // paymentIdsMatch already rejects bank-stale mismatched DTOs (T-INIT).
  if (
    input.cashCompleteResult.awaitingCashConfirmation === true &&
    paymentIdsMatch
  ) {
    return true;
  }

  return false;
}

/**
 * R1: clear persisted cash when settled COMPLETED + !awaiting and paymentId empty/mismatch.
 * KEEP when paymentIds match (paid cash until continue T6).
 * sessionSawAwaitingCash strengthens keep only when not an explicit mismatch.
 */
export function shouldClearStaleCashCollectResult(input: {
  readonly cashCompleteResult: CashPaymentStartResponseDTO | null;
  readonly journeyPaymentId: string | null;
  readonly checkoutSessionPaymentId: string | null | undefined;
  readonly paymentStatusSettled: boolean;
  readonly paymentStatus: string | undefined;
  readonly awaitingCashConfirmation: boolean | undefined;
  readonly sessionSawAwaitingCash?: boolean;
}): boolean {
  if (input.cashCompleteResult === null) {
    return false;
  }
  if (!input.paymentStatusSettled || input.paymentStatus !== 'COMPLETED') {
    return false;
  }
  if (input.awaitingCashConfirmation === true) {
    return false;
  }
  const dtoPay = trimPayId(input.cashCompleteResult.paymentId);
  if (dtoPay.length === 0) {
    return true;
  }
  const journeyPay = resolveCanonicalJourneyPaymentId(
    input.journeyPaymentId,
    input.checkoutSessionPaymentId,
  );
  const paymentIdsMatch = journeyPay.length > 0 && dtoPay === journeyPay;
  if (paymentIdsMatch) {
    return false;
  }
  if (journeyPay.length > 0 && dtoPay !== journeyPay) {
    return true;
  }
  // No canonical journey id yet — clear unless this session already saw awaiting cash.
  return input.sessionSawAwaitingCash !== true;
}

export type CashCollectShellOwnershipResult = {
  readonly shell: CashPaymentStartResponseDTO | null;
  readonly shouldClearPersisted: boolean;
};

/**
 * Single resolver for cash-collect shell passthrough + cold synthesize + R1 clear flag.
 * Preserves: bare COMPLETED never synthesizes; cashAllowed mid-handoff awaiting keep;
 * recovery holds when shell non-null; G4 clears stay in callers.
 */
export function resolveCashCollectShellOwnership(input: {
  readonly cashCompleteResult: CashPaymentStartResponseDTO | null;
  readonly journeyPaymentId: string | null;
  readonly checkoutSessionPaymentId: string | null | undefined;
  readonly checkoutSessionStatus: string | null | undefined;
  readonly paymentStatusSettled: boolean;
  readonly paymentStatus: string | undefined;
  readonly awaitingCashConfirmation: boolean | undefined;
  readonly cashRailState: CashRailState;
  readonly cashColdResumePending: boolean;
  readonly cashResumePollHint: boolean;
  readonly sessionId: string | null;
  readonly salesPointId: number | null;
  readonly amountMinor: number;
  readonly currency: string;
  readonly transactionId?: number | null;
  readonly sessionSawAwaitingCash?: boolean;
}): CashCollectShellOwnershipResult {
  const shouldClearPersisted = shouldClearStaleCashCollectResult({
    cashCompleteResult: input.cashCompleteResult,
    journeyPaymentId: input.journeyPaymentId,
    checkoutSessionPaymentId: input.checkoutSessionPaymentId,
    paymentStatusSettled: input.paymentStatusSettled,
    paymentStatus: input.paymentStatus,
    awaitingCashConfirmation: input.awaitingCashConfirmation,
    sessionSawAwaitingCash: input.sessionSawAwaitingCash,
  });

  if (input.cashCompleteResult !== null) {
    if (
      shouldExposeCashCollectShell({
        cashCompleteResult: input.cashCompleteResult,
        journeyPaymentId: input.journeyPaymentId,
        checkoutSessionPaymentId: input.checkoutSessionPaymentId,
        checkoutSessionStatus: input.checkoutSessionStatus,
        paymentStatusSettled: input.paymentStatusSettled,
        paymentStatus: input.paymentStatus,
        awaitingCashConfirmation: input.awaitingCashConfirmation,
        sessionSawAwaitingCash: input.sessionSawAwaitingCash,
      })
    ) {
      return { shell: input.cashCompleteResult, shouldClearPersisted };
    }
    // R1 — settled COMPLETED+!awaiting + empty/mismatch → treat as null for shell.
    return { shell: null, shouldClearPersisted };
  }

  // G2/R5 — cold synthesize: denied never. Allowed or unknown when poll hints.
  // Unknown + cashColdResumePending covers by-id readiness lag/403 (live stacks)
  // where paymentSurfaceReadiness is null but poll awaiting===true is authoritative.
  if (input.cashRailState === 'denied') {
    return { shell: null, shouldClearPersisted: false };
  }
  if (!input.cashResumePollHint || !input.cashColdResumePending) {
    return { shell: null, shouldClearPersisted: false };
  }
  if (input.sessionId === null || input.sessionId.length === 0) {
    return { shell: null, shouldClearPersisted: false };
  }
  if (input.salesPointId === null || input.salesPointId <= 0) {
    return { shell: null, shouldClearPersisted: false };
  }
  const paymentId = trimPayId(input.checkoutSessionPaymentId);
  if (paymentId.length === 0) {
    return { shell: null, shouldClearPersisted: false };
  }

  const synthesized: CashPaymentStartResponseDTO = {
    checkoutSessionId: input.sessionId,
    salesPointId: input.salesPointId,
    amountMinor: input.amountMinor,
    currency: input.currency,
    paymentId,
    awaitingCashConfirmation: true,
    ...(input.transactionId !== null && input.transactionId !== undefined
      ? { transactionId: input.transactionId }
      : {}),
  };
  return { shell: synthesized, shouldClearPersisted: false };
}
